import { openDB, DBSchema, IDBPDatabase } from "idb";

import {
  CommentCountUpdate,
  CommentCountData,
} from "@framework/coral/CommentCountStore";

const COMMENT_COUNT_STORE = "duet-comment-counts";
const LAST_READ_STORE = "duet-last-read";

// Database schema for TypeScript support
interface CoralDBSchema extends DBSchema {
  [COMMENT_COUNT_STORE]: {
    key: string;
    value: CommentCountData;
  };
  [LAST_READ_STORE]: {
    key: string;
    value: number;
  };
}

/**
 * Coral IndexedDB Service
 *
 * Provides IndexedDB operations for Coral comment counts and last read data.
 * Uses the idb library for promise-based IndexedDB operations with static methods.
 */

export class CoralIndexedDBUtils {
  private db: IDBPDatabase<CoralDBSchema>;
  private constructor(db: IDBPDatabase<CoralDBSchema>) {
    this.db = db;
  }

  /**
   * Get the database connection (cached, lazy initialization)
   */
  static async init(): Promise<CoralIndexedDBUtils> {
    const db = await openDB<CoralDBSchema>("duet-coral", 1, {
      upgrade(db) {
        // Create comment count store
        if (!db.objectStoreNames.contains(COMMENT_COUNT_STORE)) {
          db.createObjectStore(COMMENT_COUNT_STORE);
        }
        // Create last read store
        if (!db.objectStoreNames.contains(LAST_READ_STORE)) {
          db.createObjectStore(LAST_READ_STORE);
        }
      },
    });

    return new CoralIndexedDBUtils(db);
  }

  /**
   * Load comment counts for specific coral IDs from IndexedDB
   */
  async loadCommentCounts(coralIds: string[]): Promise<CommentCountUpdate[]> {
    if (coralIds.length === 0) {
      return [];
    }

    try {
      const updates: CommentCountUpdate[] = [];

      // Use bulk operation to get all data at once
      const tx = this.db.transaction(COMMENT_COUNT_STORE, "readonly");
      const store = tx.store;

      // Get all values in a single transaction
      const allData = await Promise.all(
        coralIds.map(async (coralId) => {
          const data = await store.get(coralId);
          return { countKey: coralId, data };
        }),
      );

      // Process results
      for (const { countKey, data } of allData) {
        if (data) {
          updates.push({
            countKey,
            count: data.count,
            timestamp: data.timestamp,
          });
        }
      }

      return updates;
    } catch (error) {
      console.warn(
        "CoralIndexedDBUtils - Failed to load comment counts:",
        error,
      );
      return [];
    }
  }

  /**
   * Save comment count updates to IndexedDB using bulk operations
   */
  async saveCommentCounts(updates: CommentCountUpdate[]): Promise<void> {
    if (updates.length === 0) {
      return;
    }

    try {
      const tx = this.db.transaction(COMMENT_COUNT_STORE, "readwrite");
      const store = tx.store;

      // Use bulk put operation
      const dataToSave = updates.map((update) => ({
        key: update.countKey,
        value: {
          count: update.count,
          timestamp: update.timestamp,
        } as CommentCountData,
      }));

      // Put all data in a single transaction
      await Promise.all(
        dataToSave.map(({ key, value }) => store.put(value, key)),
      );

      await tx.done;
    } catch (error) {
      console.warn(
        "CoralIndexedDBUtils - Failed to save comment counts:",
        error,
      );
    }
  }

  /**
   * Load all comment counts from IndexedDB
   */
  async loadAllCommentCounts(): Promise<CommentCountUpdate[]> {
    try {
      const tx = this.db.transaction(COMMENT_COUNT_STORE, "readonly");
      const store = tx.store;

      // Get all keys and values in one operation
      const [allKeys, allValues] = await Promise.all([
        store.getAllKeys(),
        store.getAll(),
      ]);

      const updates: CommentCountUpdate[] = [];

      for (let i = 0; i < allKeys.length; i++) {
        const countKey = allKeys[i];
        const data = allValues[i];

        if (data && countKey) {
          updates.push({
            countKey,
            count: data.count,
            timestamp: data.timestamp,
          });
        }
      }

      return updates;
    } catch (error) {
      console.warn(
        "CoralIndexedDBUtils - Failed to load all comment counts:",
        error,
      );
      return [];
    }
  }

  /**
   * Get the last read count for a specific coral ID from IndexedDB
   */
  async getLastReadCount(coralId: string): Promise<number | null> {
    try {
      const tx = this.db.transaction(LAST_READ_STORE, "readonly");
      const store = tx.store;

      const data = await store.get(coralId);
      return data ?? null;
    } catch (error) {
      console.warn(
        "CoralIndexedDBUtils - Failed to get last read count:",
        error,
      );
      return null;
    }
  }

  /**
   * Set the last read count for a specific coral ID in IndexedDB
   */
  async setLastReadCount(coralId: string, count: number): Promise<void> {
    try {
      const tx = this.db.transaction(LAST_READ_STORE, "readwrite");
      const store = tx.store;

      await store.put(count, coralId);
      await tx.done;
    } catch (error) {
      console.warn(
        "CoralIndexedDBUtils - Failed to set last read count:",
        error,
      );
    }
  }
}
